import type { ToolState } from '../types/LabelingTypes';
import type { DichotomousState } from './dichotomousSearch';
import { beginVerification } from './dichotomousVerification';

/**
 * Step 3's aftermath (conflict resolution) and step 4 (the bulk-apply plan) of the spec.
 *
 * Pure and framework-free, like its `dichotomousSearch.ts` / `dichotomousVerification.ts`
 * siblings: no React, no storage, no API. Only *types* are imported from `dichotomousSearch.ts`
 * (erased at build time), so this module sits at the end of the runtime dependency chain
 * `dichotomousSearch → dichotomousVerification` without closing a cycle.
 */

/** What the annotator answered in the blocking conflict dialog. */
export type ConflictChoice = 'rerun' | 'keep';

/** The exact set of writes step 4 performs: one label per image key, nothing left over. */
export interface DichotomousBulkPlan {
  ok: string[];
  worn: string[];
  /** Images set aside as unreadable — before the run or during it; never bracketed. */
  unreadable: string[];
}

// ---------- conflict resolution ----------

/**
 * Resolves a verification conflict — the two actions of the conflict dialog.
 *
 * `'keep'`: the answer that contradicted the bracket is treated as noise. It is **removed** from
 * `answers` (leaving it in would let a later recomputation act on evidence the annotator just
 * disowned), the original `lo`/`hi` stand untouched, and the search ends at `'summary'`. The rest
 * of the verification queue is abandoned rather than re-queued: choosing "keep this breakpoint"
 * *is* the decision to stop verifying.
 *
 * `'rerun'`: the answer is real information. It stays in `answers` and the bracket is recomputed
 * from the answer map (see `recomputeBounds`), which either resumes the narrowing search or —
 * when the new bracket is already adjacent — starts a fresh verification round.
 *
 * Contract: only callable in `'conflict'`, with a conflict actually recorded; anything else
 * **throws**, matching `answerDichotomous`'s out-of-contract behaviour. Never mutates `state`.
 */
export function resolveConflict(
  state: DichotomousState,
  choice: ConflictChoice,
  rand: () => number = Math.random
): DichotomousState {
  if (state.phase !== 'conflict' || state.conflict === null) {
    throw new Error(
      `resolveConflict: no conflict to resolve in phase "${state.phase}" (conflict: ${JSON.stringify(state.conflict)})`
    );
  }

  const { index, given } = state.conflict;

  // Both branches end the verification round that was in flight. `verifyTotal` is reset with the
  // queue it describes: it is only ever the denominator of a live "Verification n of total" line,
  // and an abandoned round must not leave a total behind that claims checks that never ran.
  // Every non-zero `verifyTotal` in the machine is therefore set by `beginVerification`, together
  // with the queue it counts.
  const cleared: DichotomousState = {
    ...state,
    answers: { ...state.answers },
    conflict: null,
    verifyQueue: [],
    verifyTotal: 0,
  };

  if (choice === 'keep') {
    delete cleared.answers[index];
    return { ...cleared, phase: 'summary' };
  }

  const { lo, hi } = recomputeBounds(cleared.answers, index, given);
  const narrowed: DichotomousState = { ...cleared, lo, hi };

  // Adjacent bounds leave nothing to search — the disputed answer pinned the breakpoint down on
  // its own — so the run goes straight back into verification (or to `'summary'` when nothing is
  // left to sample). Otherwise `currentIndex` derives the next midpoint from the new bracket.
  return hi - lo === 1 ? beginVerification(narrowed, rand) : { ...narrowed, phase: 'search' };
}

/**
 * Recomputes `[lo, hi]` from the answer map after a disputed answer was kept.
 *
 * The conflicting answer is the newest, most deliberate evidence available — the annotator chose
 * "re-run" precisely to stand by it — so it anchors the scan, and the bracket is then closed with
 * the nearest *supporting* answer on the other side:
 *
 * - given `'ok'` (a worn-zone image the annotator says is fine):
 *   `lo = max{i : answers[i] === 'ok'}`, `hi = min{i > lo : answers[i] === 'worn'}` — lo moves up.
 * - given `'worn'` (an ok-zone image the annotator says is worn):
 *   `hi = min{i : answers[i] === 'worn'}`, `lo = max{i < hi : answers[i] === 'ok'}` — hi moves down.
 *
 * The first case is the plan's literal formula. The second is its mirror image, and it is a
 * deliberate generalisation of the plan: applying the `max`-ok formula to an ok-zone conflict is
 * provably a no-op (in any conflict state `answers[lo] === 'ok'`, `answers[hi] === 'worn'` and
 * `hi === lo + 1`, so `max{i : ok}` is still `lo` and `hi` is unchanged), which would silently
 * discard the answer the annotator just insisted on and make "re-run search from here" do
 * nothing — while the symmetric worn-zone conflict *was* honoured. The plan's own acceptance
 * bullet ("re-run from an ok-zone conflict narrows `hi` downward") requires this mirror, and for
 * worn-zone conflicts the two formulas are provably identical (no ok answer can sit above the
 * disputed index in a conflict state, so `max{i : ok}` *is* the disputed index).
 *
 * Both bounds are total on any reachable conflict state: index `0` is answered `'ok'` and index
 * `n - 1` is answered `'worn'` before verification can start, and neither is ever a verification
 * candidate. The throw below therefore only fires on a corrupted state (e.g. a hand-edited or
 * mis-restored snapshot) — a loud failure being far preferable to a bracket built on nothing.
 */
function recomputeBounds(
  answers: Record<number, ToolState>,
  index: number,
  given: ToolState
): { lo: number; hi: number } {
  const okIndices = indicesLabeled(answers, 'ok');
  const wornIndices = indicesLabeled(answers, 'worn');

  const bounds =
    given === 'ok'
      ? anchorOnOk(okIndices, wornIndices)
      : // mirror image: anchor on the earliest worn answer, close the bracket with the last ok below it
        anchorOnWorn(okIndices, wornIndices);

  if (bounds === null) {
    throw new Error(
      `resolveConflict: cannot rebuild a bracket around index ${index} — the answer map holds no ` +
        `${given === 'ok' ? 'worn answer above its last ok one' : 'ok answer below its first worn one'}`
    );
  }

  return bounds;
}

function anchorOnOk(okIndices: number[], wornIndices: number[]): { lo: number; hi: number } | null {
  const lo = last(okIndices);
  if (lo === undefined) return null;
  const hi = wornIndices.find((index) => index > lo);
  return hi === undefined ? null : { lo, hi };
}

function anchorOnWorn(
  okIndices: number[],
  wornIndices: number[]
): { lo: number; hi: number } | null {
  const hi = wornIndices[0];
  if (hi === undefined) return null;
  const lo = last(okIndices.filter((index) => index < hi));
  return lo === undefined ? null : { lo, hi };
}

/** Answered indices carrying `label`, ascending. */
function indicesLabeled(answers: Record<number, ToolState>, label: ToolState): number[] {
  return Object.keys(answers)
    .map(Number)
    .filter((index) => answers[index] === label)
    .sort((a, b) => a - b);
}

function last(indices: number[]): number | undefined {
  return indices.length > 0 ? indices[indices.length - 1] : undefined;
}

// ---------- bulk plan ----------

/**
 * Step 4: turns the finished search into the exact list of keys to write.
 *
 * `'all-ok'` / `'all-worn'` cover the whole readable slice (the degenerate runs never produced a
 * bracket), `'empty'` writes nothing, and a converged `'summary'` splits the sequence at the
 * breakpoint: `keys[0..lo]` ok, `keys[hi..n-1]` worn — which partitions the keys exactly because
 * `lo` and `hi` are adjacent. Every phase also writes `state.unreadable` as such
 * (`'all-unreadable'` writes nothing else), so ok + worn + unreadable always partition the
 * original slice.
 *
 * That adjacency is the one property protecting the database from a half-written slice, so it is
 * asserted rather than assumed, and every non-terminal phase throws: returning a partial plan
 * mid-search would look perfectly plausible to a caller and quietly bulk-write wrong labels. A
 * caller that renders a plan continuously (the task-7 hook) must therefore gate on the phase.
 */
export function computeBulkPlan(state: DichotomousState): DichotomousBulkPlan {
  const unreadable = [...state.unreadable];

  switch (state.phase) {
    case 'all-ok':
      return { ok: [...state.keys], worn: [], unreadable };

    case 'all-worn':
      return { ok: [], worn: [...state.keys], unreadable };

    case 'all-unreadable':
    case 'empty':
      return { ok: [], worn: [], unreadable };

    case 'summary':
      if (state.hi - state.lo !== 1) {
        throw new Error(
          `computeBulkPlan: bracket [${state.lo}, ${state.hi}] is not converged — refusing to ` +
            'build a plan that would leave images unwritten'
        );
      }
      return {
        ok: state.keys.slice(0, state.lo + 1),
        worn: state.keys.slice(state.hi),
        unreadable,
      };

    default:
      throw new Error(`computeBulkPlan: phase "${state.phase}" has no finished search to apply`);
  }
}
